import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import {
    callGoogleAdsProxy,
    GOOGLE_ADS_API_VERSION_PATH,
    mutateAdGroupCriteria,
    parseAdGroupCriteriaMutateResults,
    type IGoogleAdsAdGroupCriterionRemoveOperation,
    type IGoogleAdsKeywordMutateResult,
} from "../services/google-ads-proxy-client";

export interface IUseGoogleAdsRemoveKeywordsVariables {
    customerId: string;
    loginCustomerId?: string;
    adGroupId: string;
    keywords: string[];
}

interface IAdGroupCriterionSearchResponse {
    results?: { adGroupCriterion?: { resourceName?: string; keyword?: { text?: string } } }[];
}

const normalizeKeyword = (keyword: string): string => keyword.toLowerCase().trim();

// Removal is keyed by criterion resource name, which isn't stored client-side — so first resolve
// each keyword text to its criterion resource names via a googleAds:search on the ad group. One
// text can have several criteria (BROAD / PHRASE / EXACT), and the list shows them as one row, so
// every non-negative criterion for the text must be removed. Negative criteria are excluded in the
// query so this path can never remove a negative keyword that happens to share the text.
const fetchCriterionResourceNamesByKeyword = async (
    customerId: string,
    loginCustomerId: string | undefined,
    adGroupId: string,
): Promise<Map<string, string[]>> => {
    const query = `SELECT ad_group_criterion.resource_name, ad_group_criterion.keyword.text FROM ad_group_criterion WHERE ad_group.id = ${adGroupId} AND ad_group_criterion.type = KEYWORD AND ad_group_criterion.negative = FALSE`;

    const response = await callGoogleAdsProxy<IAdGroupCriterionSearchResponse>({
        method: "POST",
        path: `${GOOGLE_ADS_API_VERSION_PATH}/customers/${customerId}/googleAds:search`,
        body: { query },
        loginCustomerId,
    });

    const resourceNamesByKeyword = new Map<string, string[]>();
    for (const result of response.results ?? []) {
        const text = result.adGroupCriterion?.keyword?.text;
        const resourceName = result.adGroupCriterion?.resourceName;
        if (!text || !resourceName) {
            continue;
        }
        const normalizedText = normalizeKeyword(text);
        const resourceNames = resourceNamesByKeyword.get(normalizedText) ?? [];
        if (!resourceNames.includes(resourceName)) {
            resourceNames.push(resourceName);
        }
        resourceNamesByKeyword.set(normalizedText, resourceNames);
    }

    return resourceNamesByKeyword;
};

// A keyword succeeds only if every one of its operations did; otherwise the worst outcome wins
// (error beats duplicate beats success) so a half-removed keyword is never reported as removed.
const OUTCOME_SEVERITY: Record<IGoogleAdsKeywordMutateResult["status"], number> = {
    success: 0,
    duplicate: 1,
    error: 2,
};

type KeywordOutcome = Omit<IGoogleAdsKeywordMutateResult, "keyword">;

const aggregateOperationResults = (operationResults: IGoogleAdsKeywordMutateResult[]): KeywordOutcome => {
    const worst = operationResults.reduce((current, candidate) =>
        OUTCOME_SEVERITY[candidate.status] > OUTCOME_SEVERITY[current.status] ? candidate : current,
    );
    return worst.status === "success" ? { status: "success" } : { status: worst.status, message: worst.message };
};

const removeGoogleAdsKeywords = async ({
    customerId,
    loginCustomerId,
    adGroupId,
    keywords,
}: IUseGoogleAdsRemoveKeywordsVariables): Promise<IGoogleAdsKeywordMutateResult[]> => {
    const resourceNamesByKeyword = await fetchCriterionResourceNamesByKeyword(customerId, loginCustomerId, adGroupId);

    // Keywords are matched case-insensitively, so two spellings of one text share the same criteria
    // and must produce a single set of operations. Keywords Google Ads no longer has (already gone /
    // never there) get a benign per-keyword result rather than failing the whole batch.
    const firstSpellingByText = new Map<string, string>();
    for (const keyword of keywords) {
        if (!firstSpellingByText.has(normalizeKeyword(keyword))) {
            firstSpellingByText.set(normalizeKeyword(keyword), keyword);
        }
    }

    const outcomeByText = new Map<string, KeywordOutcome>();
    // The parser maps results to keywords by operation index, so hand it one entry per operation.
    const operationTexts: string[] = [];
    const operations: IGoogleAdsAdGroupCriterionRemoveOperation[] = [];
    for (const [text, keyword] of firstSpellingByText) {
        const resourceNames = resourceNamesByKeyword.get(text);
        if (!resourceNames) {
            outcomeByText.set(text, { status: "error", message: "Not found in Google Ads", alreadyAbsent: true });
            continue;
        }
        for (const resourceName of resourceNames) {
            operationTexts.push(keyword);
            operations.push({ remove: resourceName });
        }
    }

    if (operations.length > 0) {
        const response = await mutateAdGroupCriteria({ customerId, loginCustomerId, operations });
        const operationResults = parseAdGroupCriteriaMutateResults(response, operationTexts);

        for (const [text, keyword] of firstSpellingByText) {
            const keywordResults = operationResults.filter((_, index) => operationTexts[index] === keyword);
            if (keywordResults.length > 0) {
                outcomeByText.set(text, aggregateOperationResults(keywordResults));
            }
        }
    }

    return keywords.map((keyword) => ({
        keyword,
        ...(outcomeByText.get(normalizeKeyword(keyword)) as KeywordOutcome),
    }));
};

/**
 * Removes one or more KEYWORD ad-group criteria (SIM-113). Unlike the create-based write hook,
 * this first resolves each keyword text to its criterion resource name via a googleAds:search,
 * then sends a `remove` operation per matching criterion in a single batched mutate. Per-keyword outcomes (success /
 * error / "not found") are returned in the resolved data, mirroring
 * `useGoogleAdsWriteKeywords` so the confirmation modal can consume either interchangeably.
 */
export const useGoogleAdsRemoveKeywords = (): UseMutationResult<
    IGoogleAdsKeywordMutateResult[],
    Error,
    IUseGoogleAdsRemoveKeywordsVariables
> => {
    return useMutation({
        mutationFn: removeGoogleAdsKeywords,
    });
};
